import { isAdminRole } from '@/helpers/roles';
import { useAppSelector } from '@/store/hooks';
import { selectIsSuperAdmin } from '@/store/selectors/me.selectors';
import { AddRounded, TuneRounded } from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo, useState } from 'react';
import DateRangeControl from '../../ReportingV2/components/DateRangeControl';
import MultiSelectFilter from '../../ReportingV2/components/MultiSelectFilter';
import { genericChannelLabel, mergeChannelOptionsForRestrictedViewer } from '../../ReportingV2/format';
import useFilterOptions, { labelCache } from '../../ReportingV2/hooks/useFilterOptions';
import {
  CUSTOM_DIMENSION_FILTER_TARGETS,
  type ContainsFilterKey,
  type DashboardFilters,
  type FilterBarConfig,
  type SavedView,
  collapseChannelSelection,
  defaultDashboardFilters,
  expandChannelSelection,
  filtersDifferFrom,
  orderedFilterBarItems,
} from '../lib/dashboardFilters';
import { useDashboardPermissions } from '../lib/dashboardPermissions';
import type { CustomDimensionDef } from '../lib/tenantFields';
import AdvancedFilterBuilder from './AdvancedFilterBuilder';
import FilterBarConfigDrawer from './FilterBarConfigDrawer';

/** Filter keys whose value is a string[] — everything except the date range. */
const LIST_KEYS = [
  'whitelabelIds',
  'clientIds',
  'advertiserIds',
  'campaignIds',
  'lineItemIds',
  'channelIds',
  'productIds',
  'deviceIds',
  'dsps',
  'creativeIds',
  'publishers',
] as const;

const EMPTY_LISTS = {
  ...Object.fromEntries(LIST_KEYS.map(k => [k, []])),
  containsFilters: {},
  excludesFilters: {},
  customDimensionFilters: {},
  booleanGroups: [],
} as Partial<DashboardFilters>;

/**
 * Dashboard-level filter bar. Selections cascade to every widget's report
 * request via applyDashboardFilters. Reuses ReportingV2's option-fetching hook
 * and MultiSelectFilter so the option value-space matches the report backend.
 *
 * Which controls appear, and in what order, comes from the dashboard's own
 * filter-bar config (see FilterBarConfigDrawer) rather than being hard-coded.
 */
const DashboardFilterBar = ({
  filters,
  dataThroughLabel,
  filterBar,
  canManageTenant,
  tenantDefaults,
  tenantError,
  views,
  activeViewId,
  customDimensions,
  onChange,
  onFilterBarChange,
  onSaveTenantDefault,
  onResetTenantDefault,
  onSaveView,
  onSelectView,
  onResetView,
  onDeleteView,
}: {
  filters: DashboardFilters;
  /** "Data through <date>" (flank#5049), or null until any widget's report
   * response has surfaced freshness data. */
  dataThroughLabel: string | null;
  filterBar: FilterBarConfig;
  canManageTenant: boolean;
  tenantDefaults: string[] | null;
  tenantError: string | null;
  views: SavedView[];
  activeViewId: string | null;
  /** Only dimensions on an enum-like field (see CUSTOM_DIMENSION_FILTER_TARGETS) get a control. */
  customDimensions: CustomDimensionDef[];
  onChange: (patch: Partial<DashboardFilters>) => void;
  onFilterBarChange: (next: FilterBarConfig) => void;
  onSaveTenantDefault: () => void;
  onResetTenantDefault: () => void;
  onSaveView: (name: string) => void;
  onSelectView: (id: string | null) => void;
  onResetView: () => void;
  onDeleteView: (id: string) => void;
}) => {
  const [newViewName, setNewViewName] = useState('');
  const [extraOpen, setExtraOpen] = useState<HTMLElement | null>(null);
  const [configOpen, setConfigOpen] = useState(false);
  // Reveal a "More" filter once it's chosen (or already has a selection).
  const [shown, setShown] = useState<Record<string, boolean>>({});
  const activeView = views.find(v => v.id === activeViewId) ?? null;
  const modified = filtersDifferFrom(filters, activeView);
  // flank#5054 — "Show DSP / Platform" (#4442): a viewer without it never
  // sees the DSP filter at all, same as Unified Reporting's own filter bar.
  const isRibeyeAdmin = useAppSelector(selectIsSuperAdmin);
  const { canViewPlatform, canViewRawInventory } = useDashboardPermissions(isRibeyeAdmin);
  // White Label (flank#5058) is admin-only, same gate and same reasoning as
  // Unified Reporting's own filter bar (#4454): Client/Agency/Advertiser users
  // don't get the control at all, not just a narrowed option list. Passing ''
  // skips the fetch entirely for everyone else.
  const role = useAppSelector(state => state.me.role);
  const canSelectWhitelabel = isAdminRole(role);
  const whitelabel = useFilterOptions(canSelectWhitelabel ? 'whitelabel' : '');
  // Client leads the row: it is the widest scope a user can pick, and the one
  // Bonneville asked for so a market can see itself across every advertiser.
  // Scoped to the selected white label(s) (#5058) — otherwise an admin's
  // Client dropdown kept listing every white label's clients regardless of
  // what they picked, same bug Unified Reporting's #4454 fixed on this list.
  const client = useFilterOptions('client', { whitelabels: filters.whitelabelIds });
  const advertiser = useFilterOptions('advertiser', {
    whitelabels: filters.whitelabelIds,
    clients: filters.clientIds,
  });
  // Campaign + Line Item list ribeye entities (#4614) — one selection spans the
  // matching DSP campaigns / line items on every DSP. Each is scoped by every
  // parent above it: white label → client → advertiser → campaign → line item.
  const entityScope = {
    whitelabels: filters.whitelabelIds,
    clients: filters.clientIds,
    advertisers: filters.advertiserIds,
  };
  const campaign = useFilterOptions('ribeye_campaign', entityScope);
  const lineItem = useFilterOptions('ribeye_line_item', { ...entityScope, ribeyeCampaignIds: filters.campaignIds });
  const channel = useFilterOptions('channel');
  // flank#5062: a viewer without "Show DSP / Platform" must not see GAM's
  // "GAM Display"/"GAM Video" channel names in the picker either — mirrors
  // Unified Reporting's own Channel chip (ReportingV2FilterBar.tsx).
  const channelOptions = useMemo(
    () => mergeChannelOptionsForRestrictedViewer(channel.options, canViewPlatform),
    [channel.options, canViewPlatform],
  );
  const device = useFilterOptions('device');
  const product = useFilterOptions('product');
  const dsp = useFilterOptions(canViewPlatform ? 'dsp' : '');
  const creative = useFilterOptions('creative');
  const publisher = useFilterOptions(canViewRawInventory ? 'publisher' : '');

  // Only dimensions on an enum-like canonical field can filter (see
  // CUSTOM_DIMENSION_FILTER_TARGETS) — one built on advertiser_name etc. gets
  // no control here yet.
  const filterableCustomDimensions = customDimensions.filter(d => CUSTOM_DIMENSION_FILTER_TARGETS[d.field]);

  const withCustomDimensionSelection = (key: string, labels: string[]): Record<string, string[]> => {
    const next = { ...filters.customDimensionFilters };
    if (labels.length) next[key] = labels;
    else delete next[key];
    return next;
  };

  // clientIds/whitelabelIds have no keyword support — both narrow tenancy in
  // the API, not a WHERE filter the backend's contains mechanism covers (same
  // carve-out as ribeye-api's FilterColumnKey).
  const isKeywordable = (key: (typeof LIST_KEYS)[number]): key is ContainsFilterKey =>
    key !== 'clientIds' && key !== 'whitelabelIds';

  // Setting [key]: undefined (instead of dropping it) would leave the key
  // behind, so Object.keys(...)-based checks (hasActive) would keep reading
  // this dimension as active after the user clears its last term.
  const withKeywordTerms = (
    map: Partial<Record<ContainsFilterKey, string[]>>,
    key: ContainsFilterKey,
    terms: string[],
  ): Partial<Record<ContainsFilterKey, string[]>> => {
    const next = { ...map };
    if (terms.length) next[key] = terms;
    else delete next[key];
    return next;
  };

  const listFilter = (
    key: (typeof LIST_KEYS)[number],
    label: string,
    opts: ReturnType<typeof useFilterOptions>,
    searchable = true,
    // Ribeye dimensions only (#4614): their values are ids, so a selection outside
    // the current list needs its remembered name rather than the raw id.
    labelOf?: (value: string) => string,
  ) => (
    <MultiSelectFilter
      label={label}
      options={opts.options}
      loading={opts.loading}
      labelOf={labelOf}
      selected={filters[key]}
      onChange={v => onChange({ [key]: v })}
      {...(searchable ? { onSearchChange: opts.setSearch } : {})}
      {...(isKeywordable(key)
        ? {
            keywordTerms: filters.containsFilters[key] ?? [],
            onKeywordTermsChange: (terms: string[]) =>
              onChange({ containsFilters: withKeywordTerms(filters.containsFilters, key, terms) }),
            excludeTerms: filters.excludesFilters[key] ?? [],
            onExcludeTermsChange: (terms: string[]) =>
              onChange({ excludesFilters: withKeywordTerms(filters.excludesFilters, key, terms) }),
          }
        : {})}
    />
  );

  const CONTROLS: Record<string, ReactNode> = {
    date: (
      <>
        {/* Same control as the Reporting filter bar, so both offer the identical
            preset list plus "Custom range…" (flank#4114 — testers set explicit
            windows like 06/28 → 07/04, which fixed presets can't express). */}
        <DateRangeControl value={filters} onChange={onChange} />
        {/* The resolved window, not just the preset name — a screenshot or PDF of
            "Last 30 Days" is undatable after the fact, the explicit range isn't.
            Suppressed for a custom range: since flank#4129 the pill itself prints
            the dates, so showing them again here was the same window twice, in two
            different formats. */}
        {filters.datePreset !== 'Custom' && (
          <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
            {filters.dateStart} → {filters.dateEnd}
          </Typography>
        )}
        {/* flank#5049: lets a viewer tell "no data yet for the last N days" apart
            from "a real decline" without cross-checking Unified Reporting. */}
        {dataThroughLabel && (
          <Typography variant='caption' color='text.secondary' sx={{ whiteSpace: 'nowrap', mr: 0.5 }}>
            {dataThroughLabel}
          </Typography>
        )}
      </>
    ),
    ...(canSelectWhitelabel ? { whitelabelIds: listFilter('whitelabelIds', 'White Label', whitelabel) } : {}),
    clientIds: listFilter('clientIds', 'Client', client),
    advertiserIds: listFilter('advertiserIds', 'Advertiser', advertiser),
    campaignIds: listFilter('campaignIds', 'Campaign', campaign, true, campaign.labelOf),
    lineItemIds: listFilter('lineItemIds', 'Line Item', lineItem, true, lineItem.labelOf),
    // flank#5062: not routed through listFilter — the merged options need
    // collapseChannelSelection/expandChannelSelection to translate between
    // the raw ids filters.channelIds stores and the masked options a
    // restricted viewer is offered.
    channelIds: (
      <MultiSelectFilter
        label='Channel'
        options={channelOptions}
        loading={channel.loading}
        // CodeRabbit: a saved channelIds value can briefly (or permanently, if
        // the channel has since been removed) match no fetched option — a
        // restricted viewer must never see that orphaned id's raw warehouse
        // value as the chip's own label, so mask it the same way a matched
        // option's label already is.
        labelOf={id => genericChannelLabel(labelCache('channel').get(id) ?? id, canViewPlatform)}
        selected={collapseChannelSelection(filters.channelIds, channelOptions)}
        onChange={v => onChange({ channelIds: expandChannelSelection(v, channelOptions, filters.channelIds) })}
        keywordTerms={filters.containsFilters.channelIds ?? []}
        onKeywordTermsChange={(terms: string[]) =>
          onChange({ containsFilters: withKeywordTerms(filters.containsFilters, 'channelIds', terms) })
        }
        excludeTerms={filters.excludesFilters.channelIds ?? []}
        onExcludeTermsChange={(terms: string[]) =>
          onChange({ excludesFilters: withKeywordTerms(filters.excludesFilters, 'channelIds', terms) })
        }
      />
    ),
    productIds: listFilter('productIds', 'Product', product),
    deviceIds: listFilter('deviceIds', 'Device', device, false),
    ...(canViewPlatform ? { dsps: listFilter('dsps', 'DSP', dsp, false) } : {}),
    creativeIds: listFilter('creativeIds', 'Creative', creative),
    // flank#5097 — same gate as DSP above: the server would silently drop a
    // `publishers` filter for a viewer without raw-inventory permission.
    ...(canViewRawInventory ? { publishers: listFilter('publishers', 'Publisher', publisher) } : {}),
  };

  // A keyword filter (containsFilters/excludesFilters) counts as a selection
  // too — otherwise a dashboard filtered ONLY by keyword would show a "more"
  // control as collapsed/empty despite actually filtering something.
  const hasSelection = (key: string) =>
    key !== 'date' &&
    ((filters[key as (typeof LIST_KEYS)[number]] ?? []).length > 0 ||
      (filters.containsFilters[key as ContainsFilterKey]?.length ?? 0) > 0 ||
      (filters.excludesFilters[key as ContainsFilterKey]?.length ?? 0) > 0);

  // flank#5097, CodeRabbit: a denied DSP/Publisher is absent from CONTROLS
  // (above) but `orderedFilterBarItems` doesn't know that — without this
  // filter, a dashboard with either set to "more" placement still lists it
  // in the "Add filter" menu, and picking it renders nothing (no control).
  const items = orderedFilterBarItems(filterBar).filter(i => CONTROLS[i.key] !== undefined);
  const pinned = items.filter(i => i.placement === 'pinned');
  const more = items.filter(i => i.placement === 'more');
  // A "More" filter appears inline once revealed, or if it already carries a
  // selection — otherwise loading a dashboard would apply a filter with no
  // visible control saying so.
  const revealed = more.filter(i => shown[i.key] || hasSelection(i.key));
  const collapsed = more.filter(i => !shown[i.key] && !hasSelection(i.key));

  // Hidden filters still apply — that's how a dashboard is pre-seeded for a
  // client. Say so, rather than silently filtering behind a missing control.
  const hiddenActive = items.filter(i => i.placement === 'hidden' && hasSelection(i.key));

  const hasActive =
    LIST_KEYS.some(k => filters[k].length > 0) ||
    Object.values(filters.containsFilters).some(terms => (terms?.length ?? 0) > 0) ||
    Object.values(filters.excludesFilters).some(terms => (terms?.length ?? 0) > 0) ||
    Object.values(filters.customDimensionFilters).some(labels => (labels?.length ?? 0) > 0) ||
    (filters.booleanGroups ?? []).some(g => g.conditions.some(c => c.values.length > 0));

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
      {pinned.map(i => (
        <Box key={i.key} sx={{ display: 'contents' }}>
          {CONTROLS[i.key]}
        </Box>
      ))}
      {revealed.map(i => (
        <Box key={i.key} sx={{ display: 'contents' }}>
          {CONTROLS[i.key]}
        </Box>
      ))}

      {filterableCustomDimensions.map(dim => (
        <MultiSelectFilter
          key={dim.key}
          label={dim.label}
          options={dim.buckets.map(b => ({ value: b.label, label: b.label }))}
          selected={filters.customDimensionFilters[dim.key] ?? []}
          onChange={labels => onChange({ customDimensionFilters: withCustomDimensionSelection(dim.key, labels) })}
        />
      ))}

      <AdvancedFilterBuilder
        groups={filters.booleanGroups ?? []}
        onChange={booleanGroups => onChange({ booleanGroups })}
        permissions={{ canViewPlatform, canViewRawInventory }}
      />

      {collapsed.length > 0 && (
        <>
          <Button
            size='small'
            startIcon={<AddRounded sx={{ fontSize: 16 }} />}
            onClick={e => setExtraOpen(e.currentTarget)}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Add filter
            <Chip label={collapsed.length} size='small' sx={{ ml: 0.75, height: 18, fontSize: 11 }} />
          </Button>
          <Menu anchorEl={extraOpen} open={Boolean(extraOpen)} onClose={() => setExtraOpen(null)}>
            {collapsed.map(i => (
              <MenuItem
                key={i.key}
                onClick={() => {
                  setShown(p => ({ ...p, [i.key]: true }));
                  setExtraOpen(null);
                }}
              >
                {i.label}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}

      {hiddenActive.length > 0 && (
        <Tooltip
          title={`Applied but not shown on this bar: ${hiddenActive.map(i => i.label).join(', ')}. Change this in Filter bar settings.`}
        >
          <Chip
            size='small'
            variant='outlined'
            color='info'
            label={`${hiddenActive.length} hidden filter${hiddenActive.length > 1 ? 's' : ''} active`}
            sx={{ height: 24 }}
          />
        </Tooltip>
      )}

      {hasActive && (
        <Button
          size='small'
          onClick={() => onChange({ ...EMPTY_LISTS })}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Clear all
        </Button>
      )}

      <Tooltip title='Filter bar settings — choose which filters this dashboard offers'>
        <IconButton size='small' onClick={() => setConfigOpen(true)} aria-label='Filter bar settings'>
          <TuneRounded fontSize='small' />
        </IconButton>
      </Tooltip>

      <Box sx={{ flexGrow: 1 }} />

      {/* ── Saved views: named filter snapshots for this dashboard ── */}
      <Divider orientation='vertical' flexItem sx={{ mx: 0.5, my: 0.5 }} />
      <Select
        size='small'
        displayEmpty
        value={activeViewId ?? ''}
        onChange={e => onSelectView(e.target.value === '' ? null : String(e.target.value))}
        renderValue={v => `Filters: ${views.find(x => x.id === v)?.name ?? 'Default'}`}
        sx={{ minWidth: 150 }}
      >
        <MenuItem value=''>Default</MenuItem>
        {views.map(v => (
          <MenuItem key={v.id} value={v.id}>
            {v.name}
          </MenuItem>
        ))}
      </Select>
      {modified && (
        <>
          <Tooltip title='The filters differ from the saved set. Reset to discard, or save as a new one. For pages with their own widgets, use Add page.'>
            <Chip label='Modified' size='small' variant='outlined' color='warning' sx={{ height: 24 }} />
          </Tooltip>
          <Button size='small' onClick={onResetView} sx={{ textTransform: 'none' }}>
            Reset
          </Button>
        </>
      )}
      {activeView && (
        <Button
          size='small'
          onClick={() => onDeleteView(activeView.id)}
          sx={{ textTransform: 'none', color: 'text.secondary' }}
        >
          Delete view
        </Button>
      )}
      <TextField
        size='small'
        placeholder='Save filters as…'
        value={newViewName}
        onChange={e => setNewViewName(e.target.value)}
        onKeyDown={e => {
          if (e.key === 'Enter' && newViewName.trim()) {
            onSaveView(newViewName.trim());
            setNewViewName('');
          }
        }}
        sx={{ width: 150 }}
      />
      <Button
        size='small'
        disabled={!newViewName.trim()}
        onClick={() => {
          onSaveView(newViewName.trim());
          setNewViewName('');
        }}
        sx={{ textTransform: 'none' }}
      >
        Save
      </Button>

      <FilterBarConfigDrawer
        open={configOpen}
        config={filterBar}
        canManageTenant={canManageTenant}
        tenantDefaults={tenantDefaults}
        tenantError={tenantError}
        onClose={() => setConfigOpen(false)}
        onChange={onFilterBarChange}
        onSaveTenantDefault={onSaveTenantDefault}
        onResetTenantDefault={onResetTenantDefault}
      />
    </Box>
  );
};

export { defaultDashboardFilters };
export default DashboardFilterBar;
